/**
 * AdminSelfServe2FA — logged-in admin self-enables TOTP 2FA.
 *
 * Reuses the existing backend endpoints:
 *   GET  /api/admin/2fa/status  → {totp_enabled, has_recovery_codes}
 *   POST /api/admin/2fa/setup   → {qr_code, secret, provisioning_uri}
 *   POST /api/admin/2fa/enable  → {enabled, recovery_codes}
 *
 * No forced-enrollment, no schema change. Renders:
 *   - status pill ("2FA enabled" / "2FA off")
 *   - "Enable 2FA" button (if off) → opens dialog with QR → TOTP verify →
 *     recovery codes
 *   - when already enabled: info note + how to reset (via another admin)
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  ShieldCheck, ShieldOff, Loader2, Copy, Check, ArrowRight, KeyRound, AlertTriangle,
} from "lucide-react";
export default function AdminSelfServe2FA() {
  const [status, setStatus] = useState(null); // {totp_enabled, has_recovery_codes}
  const [loadingStatus, setLoadingStatus] = useState(true);

  // Dialog state
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("qr"); // qr → recovery
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const refreshStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/2fa/status");
      setStatus(data);
    } catch {
      setStatus(null);
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  useEffect(() => { refreshStatus(); }, [refreshStatus]);

  const openDialog = async () => {
    setStep("qr"); setCode(""); setError(""); setRecoveryCodes([]);
    setQrCode(""); setSecret("");
    setOpen(true);
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/2fa/setup", {});
      setQrCode(data.qr_code);
      setSecret(data.secret);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to initialize 2FA setup");
      setOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/2fa/enable", { code });
      setRecoveryCodes(data.recovery_codes || []);
      setStep("recovery");
    } catch (err) {
      setError(err?.response?.data?.detail || "Invalid code. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const copyRecoveryCodes = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join("\n"));
      setCopied(true);
      toast.success("Recovery codes copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Clipboard unavailable — select and copy manually");
    }
  };

  const finish = async () => {
    setOpen(false);
    await refreshStatus();
    toast.success("2FA enabled. Next sign-in will require your authenticator code.");
  };

  return (
    <div className="mb-8" data-testid="admin-self-2fa-section">
      <h2 className="text-sm font-semibold text-[#111111] mb-3 uppercase tracking-wide">
        My 2FA
      </h2>
      <div className="border border-[#E5E5E5] rounded-lg bg-white px-4 py-4 flex items-center gap-4">
        <div className={`w-10 h-10 rounded-lg border flex items-center justify-center shrink-0 ${
          status?.totp_enabled
            ? "bg-green-50 border-green-200"
            : "bg-amber-50 border-amber-200"
        }`}>
          {status?.totp_enabled
            ? <ShieldCheck size={18} className="text-green-600" />
            : <ShieldOff size={18} className="text-amber-600" />}
        </div>
        <div className="flex-1 min-w-0">
          {loadingStatus ? (
            <p className="text-sm text-[#888888]">Loading status…</p>
          ) : status?.totp_enabled ? (
            <>
              <p className="text-sm font-medium text-[#111111]" data-testid="admin-self-2fa-status">
                2FA enabled
              </p>
              <p className="text-[11px] text-[#888888] mt-0.5">
                Your authenticator code is required at every admin sign-in.
                To reset 2FA, ask another admin to use the Reset Admin 2FA action on your user row.
              </p>
            </>
          ) : (
            <>
              <p className="text-sm font-medium text-[#111111]" data-testid="admin-self-2fa-status">
                2FA is off
              </p>
              <p className="text-[11px] text-[#888888] mt-0.5">
                Add an authenticator code to your admin sign-in. Strongly recommended for superadmins.
              </p>
            </>
          )}
        </div>
        {!loadingStatus && !status?.totp_enabled && (
          <Button
            size="sm"
            className="h-8 text-xs bg-zinc-900 hover:bg-zinc-800 text-white shrink-0"
            onClick={openDialog}
            data-testid="admin-self-2fa-enable-btn"
          >
            Enable 2FA
          </Button>
        )}
      </div>

      <Dialog open={open} onOpenChange={(v) => { if (!submitting) setOpen(v); }}>
        <DialogContent className="sm:max-w-md" data-testid="admin-self-2fa-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck size={18} className="text-zinc-900" />
              {step === "qr" ? "Set up 2FA" : "Save recovery codes"}
            </DialogTitle>
            <DialogDescription>
              {step === "qr"
                ? "Scan the QR in your authenticator app, then enter the 6-digit code to confirm."
                : "Store these recovery codes somewhere safe. They can't be shown again."}
            </DialogDescription>
          </DialogHeader>

          {step === "qr" && (
            <form onSubmit={handleVerify} className="space-y-4" data-testid="admin-self-2fa-qr-step">
              {qrCode ? (
                <div className="flex justify-center">
                  <img
                    src={qrCode}
                    alt="2FA QR"
                    className="w-44 h-44 rounded-md border border-[#E5E5E5]"
                    data-testid="admin-self-2fa-qr-image"
                  />
                </div>
              ) : (
                <div className="flex justify-center items-center h-44">
                  <Loader2 size={20} className="animate-spin text-[#888888]" />
                </div>
              )}

              {secret && (
                <div
                  className="rounded-lg border border-blue-200 bg-blue-50/60 p-3 space-y-2.5"
                  data-testid="admin-self-2fa-manual-key"
                >
                  <div className="flex items-start gap-2">
                    <KeyRound size={14} className="text-blue-600 shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-blue-700">
                        Or enter this code manually
                      </p>
                      <p className="text-[11px] text-[#555555] mt-0.5">
                        Enter this key in your authenticator app to generate login codes.
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2 sm:items-stretch">
                    <div className="flex-1 rounded-md border-2 border-dashed border-blue-300 bg-white px-3 py-2.5">
                      <code
                        className="block font-mono text-sm sm:text-base tracking-wider text-[#111111] break-all select-all text-center"
                        data-testid="admin-self-2fa-manual-key-value"
                      >
                        {secret}
                      </code>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      className="h-auto sm:w-24 bg-blue-600 hover:bg-blue-700 text-white shrink-0"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(secret);
                          setCopied(true);
                          toast.success("Key copied");
                          setTimeout(() => setCopied(false), 2000);
                        } catch {
                          toast.error("Clipboard unavailable — tap the key to select and copy manually");
                        }
                      }}
                      data-testid="admin-self-2fa-manual-key-copy"
                    >
                      {copied ? <Check size={13} className="mr-1.5" /> : <Copy size={13} className="mr-1.5" />}
                      {copied ? "Copied" : "Copy"}
                    </Button>
                  </div>

                  <p className="flex items-start gap-1.5 text-[11px] text-blue-700">
                    <span aria-hidden className="font-semibold">ⓘ</span>
                    <span>Store this key securely. You'll need it if you can't scan the QR code.</span>
                  </p>
                </div>
              )}

              <div>
                <label className="text-xs text-[#555555] block mb-1.5">6-digit code</label>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="text-center text-base tracking-[0.4em] font-mono h-11"
                  autoFocus
                  data-testid="admin-self-2fa-code-input"
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2.5 py-1.5" data-testid="admin-self-2fa-error">
                  <AlertTriangle size={12} className="shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <DialogFooter className="sm:justify-between gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setOpen(false)}
                  disabled={submitting}
                  data-testid="admin-self-2fa-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  className="bg-zinc-900 hover:bg-zinc-800 text-white"
                  disabled={submitting || code.length !== 6 || !qrCode}
                  data-testid="admin-self-2fa-verify"
                >
                  {submitting ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : null}
                  Verify &amp; enable
                </Button>
              </DialogFooter>
            </form>
          )}

          {step === "recovery" && (
            <div className="space-y-4" data-testid="admin-self-2fa-recovery-step">
              <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200">
                <KeyRound size={15} className="text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-800">
                  Save these recovery codes. Each works exactly once and can be used instead of a TOTP code if you lose your authenticator device.
                </p>
              </div>

              <div className="bg-[#F5F5F5] rounded-md p-3 grid grid-cols-2 gap-2" data-testid="admin-self-2fa-recovery-grid">
                {recoveryCodes.map((c, i) => (
                  <code
                    key={i}
                    className="text-[11px] font-mono text-[#555555] bg-white rounded px-2 py-1 border border-[#E5E5E5] text-center"
                    data-testid={`admin-self-2fa-recovery-code-${i}`}
                  >
                    {c}
                  </code>
                ))}
              </div>

              <Button
                variant="outline"
                size="sm"
                className="w-full text-xs"
                onClick={copyRecoveryCodes}
                data-testid="admin-self-2fa-copy-recovery"
              >
                {copied ? <Check size={12} className="mr-1.5" /> : <Copy size={12} className="mr-1.5" />}
                {copied ? "Copied!" : "Copy all codes"}
              </Button>

              <DialogFooter>
                <Button
                  size="sm"
                  className="w-full bg-zinc-900 hover:bg-zinc-800 text-white"
                  onClick={finish}
                  data-testid="admin-self-2fa-finish"
                >
                  I've saved them
                  <ArrowRight size={13} className="ml-1.5" />
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
